import { useState, useEffect } from "react";
import { X, Heart, EyeOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { useHaptics } from "@/hooks/useHaptics";
import { useToast } from "@/hooks/use-toast";
import { logger } from "@/lib/logger";

interface RunMatchNotificationProps {
  encounterId: string;
  heartIndex: number;
  distance: number;
  onAccept: () => void;
  onDecline: () => void;
  onGoInvisible: () => void;
}

export const RunMatchNotification = ({
  encounterId,
  heartIndex,
  distance,
  onAccept,
  onDecline,
  onGoInvisible,
}: RunMatchNotificationProps) => {
  const [timeLeft, setTimeLeft] = useState(60);
  const { success, medium } = useHaptics();
  const { toast } = useToast();

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          onDecline();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [onDecline]);

  const handleAccept = async () => {
    success();
    
    try {
      // TODO: Integrar com Supabase quando types.ts for atualizado
      onAccept();
      
      toast({
        title: "🤝 Match Aceito!",
        description: "Aguardando resposta do outro corredor...",
      });
    } catch (error) {
      logger.error('Error accepting RunMatch', error, { component: 'RunMatchNotification', action: 'acceptMatch', encounterId });
    }
  };

  const handleDecline = () => {
    medium();
    onDecline();
  };

  const handleGoInvisible = async () => {
    medium();
    
    try {
      // TODO: Integrar com Supabase quando types.ts for atualizado
      onGoInvisible();
      
      toast({
        title: "👁️ Modo Invisível Ativado",
        description: "Você não receberá mais notificações de RunMatch.",
      });
    } catch (error) {
      logger.error('Error going invisible', error, { component: 'RunMatchNotification', action: 'goInvisible' });
    }
  };

  return (
    <div className="fixed inset-x-0 top-20 z-50 flex justify-center px-4 animate-slide-down">
      <Card className="w-full max-w-md p-6 bg-card/95 backdrop-blur-lg border-primary/20 shadow-2xl">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center gap-3">
            <Avatar className="w-12 h-12 ring-2 ring-primary/50">
              <AvatarFallback className="bg-gradient-to-br from-primary to-primary-glow">
                <Heart className="w-6 h-6 text-white" />
              </AvatarFallback>
            </Avatar>
            <div>
              <h3 className="text-lg font-bold">RunMatch Detectado! 🏃‍♂️</h3>
              <p className="text-sm text-muted-foreground">
                Corredor a {distance.toFixed(0)}m de você
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleDecline}
            className="h-8 w-8"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        <div className="mb-6">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium">HEART Index™</span>
            <span className="text-2xl font-bold bg-gradient-to-r from-primary to-primary-glow bg-clip-text text-transparent">
              {heartIndex}%
            </span>
          </div>
          <Progress value={heartIndex} className="h-3" />
          <p className="text-xs text-muted-foreground mt-2">
            Compatibilidade de ritmo, intensidade e energia
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-4 text-xs text-muted-foreground">
          <span>Expira em {timeLeft}s</span>
        </div>

        <div className="space-y-2">
          <Button
            onClick={handleAccept}
            className="w-full h-12 text-base font-semibold"
          >
            🤝 Aceitar Match
          </Button>
          
          <div className="grid grid-cols-2 gap-2">
            <Button
              onClick={handleDecline}
              variant="outline"
              className="h-10"
            >
              Agora não
            </Button>
            <Button
              onClick={handleGoInvisible}
              variant="outline"
              className="h-10"
            >
              <EyeOff className="w-4 h-4 mr-2" />
              Invisível
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
};
